<template>
  <div>
    <b-row v-if="hasPendingRequest" class="mb-2">
      <b-col cols="12">
        <div class="premium-status-flag">
          <div class="flag-content">
            <div class="flag-title">
              Vous avez une demande en attente d'approbation
            </div>
            <span class="flag-detail-badge">Désactivation du compte</span>
            <div class="flag-description">
              Soumise le {{ formatDate(pendingRequest.created_at) }}.
              Une seule demande à la fois est autorisée. Annulez celle-ci pour en soumettre une nouvelle,
              ou attendez la validation de l'administrateur.
            </div>
          </div>
          <b-button
            variant="outline-warning"
            size="sm"
            class="flag-action-btn"
            :disabled="isCancelling"
            @click="cancelPendingRequest"
          >
            <b-spinner v-if="isCancelling" small class="mr-50" />
            <feather-icon v-else icon="XIcon" size="12" class="mr-50" />
            Annuler ma demande
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col cols="12">
        <h2 class="font-weight-bold text-primary mb-1">
          Demande de désactivation du compte
        </h2>
        <p class="text-muted">
          Soumettez une demande de désactivation de votre compte. Un administrateur l'examinera avant toute action.
        </p>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="8" md="10">
        <b-card class="shadow-sm border-0 rounded-lg">
          <validation-observer ref="disableForm">
            <b-form @submit.prevent="confirmAndSubmit">
              <validation-provider
                #default="{ errors }"
                name="Motif"
                rules="required|min:10"
              >
                <b-form-group
                  label="Motif de la demande (optionnel mais recommandé)"
                  label-for="reason"
                  class="mb-2"
                >
                  <b-form-textarea
                    id="reason"
                    v-model="form.reason"
                    :state="errors.length > 0 ? false : null"
                    rows="5"
                    :disabled="hasPendingRequest"
                    placeholder="Expliquez brièvement pourquoi vous souhaitez désactiver votre compte..."
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <b-form-group class="mt-2">
                <validation-provider
                  #default="{ errors }"
                  name="Confirmation"
                  rules="required"
                >
                  <b-form-checkbox
                    id="understand"
                    v-model="form.understand"
                    :state="errors.length > 0 ? false : null"
                    :disabled="hasPendingRequest"
                  >
                    Je comprends que cette demande sera soumise à validation administrative et que je ne pourrai plus me connecter si elle est acceptée.
                  </b-form-checkbox>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

          <div class="d-flex justify-content-end mt-3">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="loading"
              @click="resetForm"
            >
              Annuler
            </b-button>
            <b-button
              variant="danger"
              type="submit"
              :disabled="loading || !canSubmit || hasPendingRequest"
            >
              <b-spinner v-if="loading" small class="mr-1" />
              <feather-icon
                v-else
                icon="UserXIcon"
                class="mr-50"
              />
              Soumettre la demande
            </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard, BRow, BCol, BButton, BForm, BFormGroup, BFormTextarea, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormTextarea,
    BFormCheckbox,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      loading: false,
      form: {
        reason: '',
        understand: false,
      },
      requests: [],
      isLoadingRequests: false,
      isCancelling: false,
    }
  },
  computed: {
    canSubmit() {
      const reason = (this.form.reason || '').trim()
      return reason.length >= 10 && this.form.understand
    },
    pendingRequest() {
      return this.requests.find(r => r.status === 'pending') || null
    },
    hasPendingRequest() {
      return this.pendingRequest !== null
    },
  },
  created() {
    this.loadRequests()
  },
  methods: {
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      }, { position: 'top-center' })
    },
    resetForm() {
      this.form.reason = ''
      this.form.understand = false
      this.$nextTick(() => {
        if (this.$refs.disableForm) {
          this.$refs.disableForm.reset()
        }
      })
    },
    async loadRequests() {
      this.isLoadingRequests = true
      try {
        const { data } = await axios.get('/api/auth/account-disable-requests/')
        this.requests = Array.isArray(data) ? data : data.results || []
      } catch (error) {
        // fail silently — the form can still be submitted
      } finally {
        this.isLoadingRequests = false
      }
    },

    async cancelPendingRequest() {
      if (!this.pendingRequest) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous vraiment annuler votre demande de désactivation en attente ?',
        {
          title: 'Annuler la demande',
          okTitle: 'Annuler',
          cancelTitle: 'Retour',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return

      this.isCancelling = true
      try {
        await axios.delete(`/api/auth/account-disable-requests/${this.pendingRequest.id}/cancel/`)
        this.showToast('success', 'Votre demande a été annulée.')
        this.resetForm()
        await this.loadRequests()
      } catch (error) {
        const detail = error?.response?.data?.detail
        let message = 'Erreur lors de l\'annulation de la demande.'
        if (detail) {
          message = Array.isArray(detail) ? detail.join(' / ') : String(detail)
        }
        this.showToast('danger', message)
      } finally {
        this.isCancelling = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    async confirmAndSubmit() {
      const valid = await this.$refs.disableForm.validate()
      if (!valid) {
        return
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous vraiment demander la désactivation de votre compte ? Cette action est soumise à validation administrative.',
        {
          title: 'Confirmer la demande',
          okTitle: 'Confirmer',
          cancelTitle: 'Annuler',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return

      this.loading = true
      try {
        await axios.post('/api/auth/account-disable-requests/', {
          reason: this.form.reason,
        })
        this.showToast('success', 'Votre demande de désactivation a été envoyée avec succès.')
        this.resetForm()
        await this.loadRequests()
      } catch (error) {
        let message = 'Erreur lors de l\'envoi de la demande.'
        const responseData = error?.response?.data
        if (responseData) {
          if (responseData.detail) {
            message = Array.isArray(responseData.detail)
              ? responseData.detail.join(' / ')
              : String(responseData.detail)
          } else {
            const flattened = Object.values(responseData).flat()
            if (flattened.length > 0) {
              message = flattened.join(' / ')
            }
          }
        }
        this.showToast('danger', message)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* Premium pending request flag (matches Informations sensibles style) */
.premium-status-flag {
  background: rgba(255, 159, 67, 0.06);
  border: 1px solid rgba(255, 159, 67, 0.15);
  border-left: 4px solid #FF9F43;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 15px rgba(255, 159, 67, 0.05);
  margin-bottom: 2rem;

  .flag-title {
    color: #cc7414;
    font-size: 1.05rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: '';
      display: block;
      width: 8px;
      height: 8px;
      background-color: #FF9F43;
      border-radius: 50%;
      box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.7);
      animation: pulse-orange 2s infinite;
    }
  }

  .flag-detail-badge {
    background: white;
    border: 1px solid rgba(255, 159, 67, 0.3);
    color: #e07f15;
    padding: 0.35rem 0.75rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.85rem;
    display: inline-block;
    margin-top: 0.75rem;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.02);
  }

  .flag-description {
    color: #82868b;
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }

  .flag-action-btn {
    border-radius: 20px;
    font-weight: 600;
    letter-spacing: 0.3px;
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
}

@keyframes pulse-orange {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 159, 67, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(255, 159, 67, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 159, 67, 0); }
}
</style>
